/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * MenuSelectorModal — Tree-based menu item picker for the Aggregator module.
 * Allows managers to select items from their Menu Management profiles
 * and import them into a campaign submission.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  X, Search, ChevronRight, ChevronDown, Check, Package, FolderOpen,
  ImageIcon, BookOpen, Loader2,
} from 'lucide-react';
import { cn } from '../../../ui/utils';
import { getMediaUrl } from '../../../core/fileUpload';
import type { User } from '../../../domain/entities';
import type { MenuProfile, MenuCategory, MenuItem } from '../../menu-manage/types';
import { isUnassignedCat } from '../../menu-manage/utils';
import type { SubmissionItem } from '../types';
import { getMenuProfilesForUser, mapMenuItemToSubmissionItem } from '../services/MenuIntegrationService';

// ─── Props ──────────────────────────────────────────────────────────────────────

interface MenuSelectorModalProps {
  open: boolean;
  onClose: () => void;
  onImport: (items: SubmissionItem[], profileName: string) => void;
  user: User | null;
  /** Brand ID filter — only show profiles for this brand */
  brandId?: string;
}

// ─── Component ──────────────────────────────────────────────────────────────────

export function MenuSelectorModal({ open, onClose, onImport, user, brandId }: MenuSelectorModalProps) {
  const [profiles, setProfiles] = useState<MenuProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Fetch profiles on open
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setSelectedProfileId('');
    setSearchQuery('');
    setSelectedItemIds(new Set());
    setExpandedCategories(new Set());

    const brandFilter = brandId ? [brandId] : undefined;
    getMenuProfilesForUser(user, brandFilter).then(p => {
      setProfiles(p);
      setLoading(false);
      // Auto-select if only one profile
      if (p.length === 1) {
        setSelectedProfileId(p[0].id);
        setExpandedCategories(new Set(p[0].categories.filter(c => !isUnassignedCat(c.id)).map(c => c.id)));
      }
    });
  }, [open, user, brandId]);

  const selectedProfile = profiles.find(p => p.id === selectedProfileId) ?? null;

  // Flatten all menu items from the selected profile for search/selection
  const allItems = useMemo(() => {
    if (!selectedProfile) return [];
    const items: { item: MenuItem; category: MenuCategory }[] = [];
    for (const cat of selectedProfile.categories) {
      if (cat.hidden || isUnassignedCat(cat.id)) continue;
      for (const item of cat.items) {
        if (item.hidden) continue;
        items.push({ item, category: cat });
      }
    }
    return items;
  }, [selectedProfile]);

  // Filter items by search query
  const filteredCategories = useMemo(() => {
    if (!selectedProfile) return [];
    const q = searchQuery.toLowerCase().trim();
    return selectedProfile.categories
      .filter(c => !c.hidden && !isUnassignedCat(c.id))
      .map(cat => ({
        ...cat,
        items: cat.items.filter(item => {
          if (item.hidden) return false;
          if (!q) return true;
          return (
            item.nameEN.toLowerCase().includes(q) ||
            item.nameAR.toLowerCase().includes(q) ||
            (item.descriptionEN ?? '').toLowerCase().includes(q)
          );
        }),
      }))
      .filter(cat => cat.items.length > 0);
  }, [selectedProfile, searchQuery]);

  const toggleCategory = (catId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      next.has(catId) ? next.delete(catId) : next.add(catId);
      return next;
    });
  };

  const toggleItem = (itemId: string) => {
    setSelectedItemIds(prev => {
      const next = new Set(prev);
      next.has(itemId) ? next.delete(itemId) : next.add(itemId);
      return next;
    });
  };

  const toggleAllInCategory = (cat: MenuCategory) => {
    const visibleIds = cat.items.filter(i => !i.hidden).map(i => i.id);
    const allSelected = visibleIds.every(id => selectedItemIds.has(id));
    setSelectedItemIds(prev => {
      const next = new Set(prev);
      if (allSelected) {
        visibleIds.forEach(id => next.delete(id));
      } else {
        visibleIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const handleImport = () => {
    if (!selectedProfile || selectedItemIds.size === 0) return;
    const selected: SubmissionItem[] = [];
    for (const { item } of allItems) {
      if (selectedItemIds.has(item.id)) {
        selected.push(mapMenuItemToSubmissionItem(item, selectedProfile));
      }
    }
    onImport(selected, selectedProfile.name);
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden border border-zinc-200">
        {/* ── Header ── */}
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between bg-gradient-to-r from-emerald-50 to-teal-50">
          <div>
            <h2 className="text-lg font-bold text-zinc-900 flex items-center gap-2">
              <BookOpen size={18} className="text-emerald-600" />
              Import from Menu
            </h2>
            <p className="text-xs text-zinc-500 mt-0.5">Select items from your master menu to auto-fill campaign data</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-zinc-100 text-zinc-400 hover:text-zinc-600 transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={24} className="animate-spin text-emerald-600" />
              <span className="ml-3 text-sm text-zinc-500">Loading menu profiles…</span>
            </div>
          ) : profiles.length === 0 ? (
            <div className="text-center py-16">
              <BookOpen size={36} className="mx-auto text-zinc-300 mb-3" />
              <p className="text-sm font-semibold text-zinc-500">No Menu Profiles Found</p>
              <p className="text-xs text-zinc-400 mt-1">There are no menu profiles available for the selected brand.</p>
            </div>
          ) : (
            <>
              {/* Step 1: Profile Selector */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Menu Profile</label>
                <select
                  value={selectedProfileId}
                  onChange={e => {
                    setSelectedProfileId(e.target.value);
                    setSearchQuery('');
                    setSelectedItemIds(new Set());
                    const p = profiles.find(pr => pr.id === e.target.value);
                    if (p) setExpandedCategories(new Set(p.categories.filter(c => !isUnassignedCat(c.id)).map(c => c.id)));
                  }}
                  className="w-full px-4 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:border-emerald-500 transition-colors"
                >
                  <option value="">Select a menu profile…</option>
                  {profiles.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.name} — {p.brandName} ({p.categories.filter(c => !isUnassignedCat(c.id)).reduce((n, c) => n + c.items.length, 0)} items)
                    </option>
                  ))}
                </select>
              </div>

              {/* Step 2: Search + Tree View */}
              {selectedProfile && (
                <>
                  <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                    <input
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      placeholder="Search items by name (EN/AR)…"
                      className="w-full pl-9 pr-4 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:border-emerald-500 transition-colors"
                    />
                  </div>

                  {/* Quick stats */}
                  <div className="flex items-center gap-3 text-xs text-zinc-400">
                    <span>{filteredCategories.length} categories</span>
                    <span>•</span>
                    <span>{filteredCategories.reduce((n, c) => n + c.items.length, 0)} items</span>
                    {selectedItemIds.size > 0 && (
                      <>
                        <span>•</span>
                        <span className="text-emerald-600 font-bold">{selectedItemIds.size} selected</span>
                      </>
                    )}
                  </div>

                  {/* Tree View */}
                  <div className="border border-zinc-200 rounded-xl overflow-hidden divide-y divide-zinc-100 max-h-[40vh] overflow-y-auto">
                    {filteredCategories.length === 0 ? (
                      <div className="text-center py-8">
                        <Search size={24} className="mx-auto text-zinc-300 mb-2" />
                        <p className="text-sm text-zinc-400">No items match your search.</p>
                      </div>
                    ) : (
                      filteredCategories.map(cat => {
                        const expanded = expandedCategories.has(cat.id);
                        const catItemIds = cat.items.map(i => i.id);
                        const selectedInCat = catItemIds.filter(id => selectedItemIds.has(id)).length;
                        const allSelectedInCat = selectedInCat === cat.items.length;

                        return (
                          <div key={cat.id}>
                            {/* Category header */}
                            <div
                              className="flex items-center gap-2 px-4 py-2.5 bg-zinc-50 hover:bg-zinc-100 cursor-pointer transition-colors"
                              onClick={() => toggleCategory(cat.id)}
                            >
                              {expanded ? <ChevronDown size={14} className="text-zinc-400" /> : <ChevronRight size={14} className="text-zinc-400" />}
                              <FolderOpen size={14} className="text-amber-500" />
                              <span className="text-sm font-semibold text-zinc-700 flex-1">{cat.nameEN}</span>
                              {cat.nameAR && <span className="text-xs text-zinc-400 font-arabic">{cat.nameAR}</span>}
                              <span className="text-[10px] text-zinc-400 font-mono">{cat.items.length}</span>
                              {/* Select all in category */}
                              <button
                                type="button"
                                onClick={e => { e.stopPropagation(); toggleAllInCategory(cat); }}
                                className={cn(
                                  "ml-2 w-5 h-5 rounded border flex items-center justify-center transition-colors",
                                  allSelectedInCat
                                    ? "bg-emerald-600 border-emerald-600 text-white"
                                    : selectedInCat > 0
                                      ? "bg-emerald-100 border-emerald-400 text-emerald-600"
                                      : "border-zinc-300 text-transparent hover:border-zinc-400"
                                )}
                              >
                                <Check size={12} />
                              </button>
                            </div>

                            {/* Items */}
                            {expanded && (
                              <div className="divide-y divide-zinc-50">
                                {cat.items.map(item => {
                                  const selected = selectedItemIds.has(item.id);
                                  const hasModifiers = (item.optionGroups ?? []).filter(g => !g.hidden).length > 0;
                                  return (
                                    <div
                                      key={item.id}
                                      className={cn(
                                        "flex items-center gap-3 px-4 pl-10 py-2.5 cursor-pointer transition-colors",
                                        selected ? "bg-emerald-50" : "hover:bg-zinc-50"
                                      )}
                                      onClick={() => toggleItem(item.id)}
                                    >
                                      {/* Checkbox */}
                                      <div className={cn(
                                        "w-5 h-5 rounded border flex items-center justify-center shrink-0 transition-colors",
                                        selected ? "bg-emerald-600 border-emerald-600 text-white" : "border-zinc-300"
                                      )}>
                                        {selected && <Check size={12} />}
                                      </div>

                                      {/* Thumbnail */}
                                      {item.image ? (
                                        <img src={getMediaUrl(item.image)} alt="" className="w-9 h-9 rounded-lg object-cover border border-zinc-200 shrink-0" />
                                      ) : (
                                        <div className="w-9 h-9 rounded-lg border border-zinc-200 bg-zinc-100 flex items-center justify-center shrink-0">
                                          <ImageIcon size={14} className="text-zinc-300" />
                                        </div>
                                      )}

                                      {/* Name + details */}
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-zinc-800 truncate">{item.nameEN}</p>
                                        <div className="flex items-center gap-2 mt-0.5">
                                          {item.nameAR && (
                                            <span className="text-[10px] text-zinc-400 font-arabic truncate">{item.nameAR}</span>
                                          )}
                                          {hasModifiers && (
                                            <span className="text-[9px] px-1.5 py-0.5 bg-indigo-50 text-indigo-600 rounded font-bold">
                                              {(item.optionGroups ?? []).filter(g => !g.hidden).length} modifiers
                                            </span>
                                          )}
                                        </div>
                                      </div>

                                      {/* Price */}
                                      <span className="text-sm font-mono font-bold text-zinc-600 shrink-0">
                                        {item.basePrice.toFixed(2)}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="px-6 py-3 border-t border-zinc-200 bg-zinc-50 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-zinc-500 hover:text-zinc-700 hover:bg-zinc-100 rounded-xl transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={selectedItemIds.size === 0}
            className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-bold hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            <Package size={14} />
            Add {selectedItemIds.size > 0 ? `${selectedItemIds.size} Item${selectedItemIds.size > 1 ? 's' : ''}` : 'Items'}
          </button>
        </div>
      </div>
    </div>
  );
}
